import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import styled from 'styled-components'

import SavedItemsList from './SavedItemsList'
import CreateTrip from '../TripModal/Trips/CreateTrip/CreateTripMC'

import {
  tripDrawerTripAllSavedItemsView,
  tripDrawerTripPlansView,
  tripPlansCreateNewTripClicked,
} from 'analytics/eventDefinitions'
import {
  fireInteractionEvent,
  fireNonInteractionEvent,
} from 'api/googleTagManager'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import LineCalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import LineHeartIcon from 'components/Luxkit/Icons/line/LineHeartIcon'
import LinePlusCircleIcon from 'components/Luxkit/Icons/line/LinePlusCircleIcon'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import TabButton from 'components/Luxkit/Tabs/TabButton'
import TabButtonsGroup from 'components/Luxkit/Tabs/TabButtonsGroup'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import config from 'constants/config'
import ModalContext from 'contexts/ModalContext'
import { useTrips } from 'tripPlanner/hooks/api'
import { useHasTripItems } from 'tripPlanner/hooks/api/tripItem'
import MyTripsEmpty from 'tripPlanner/pages/myTrips/MyTripsEmpty'
import MyTripsListGrid from 'tripPlanner/pages/myTrips/MyTripsListGrid'

const EmptySavedItemsContainer = styled(Group)`
  padding: ${rem(80)} 0;
  margin: ${rem(20)} ${rem(20)};

  ${mediaQueryUp.sm} {
    padding: ${rem(80)} ${rem(24)};
    margin: ${rem(20)} ${rem(80)};
  }
`

function SavedDrawer() {
  const [selectedTab, setSelectedTab] = useState<'trip-plans' | 'saved-items'>(
    'trip-plans',
  )
  const showModal = useContext(ModalContext)

  const createNewTrip = useCallback(() => {
    fireInteractionEvent(tripPlansCreateNewTripClicked('trip_drawer'))
    showModal(<CreateTrip />)
  }, [showModal])

  const selectAllSavedItems = useCallback(() => {
    setSelectedTab('saved-items')
  }, [])

  const selectTripPlans = useCallback(() => {
    setSelectedTab('trip-plans')
  }, [])
  const { data: trips, isLoading } = useTrips()

  useEffect(() => {
    if (selectedTab === 'trip-plans') {
      fireNonInteractionEvent(tripDrawerTripPlansView())
    } else {
      fireNonInteractionEvent(tripDrawerTripAllSavedItemsView())
    }
  }, [selectedTab])

  const hasTripItems = useHasTripItems()

  return (
    <ModalBase mode="drawer" size="large">
      <ModalHeader title="Saved" />
      <ModalBody>
        <ModalContent>
          <Group direction="vertical" gap={32}>
            <TabButtonsGroup>
              <TabButton
                size="large"
                isActive={selectedTab === 'trip-plans'}
                onClick={selectTripPlans}
                startIcon={<LineCalendarIcon />}
              >
                Trip plans
              </TabButton>
              {config.TRIP_PLANNER_DRAWER_SAVED_ITEMS && (
                <TabButton
                  size="large"
                  isActive={selectedTab === 'saved-items'}
                  onClick={selectAllSavedItems}
                  startIcon={<LineHeartIcon />}
                >
                  All saved items
                </TabButton>
              )}
            </TabButtonsGroup>
            {isLoading && <LoadingIndicator inline />}
            {!isLoading &&
              selectedTab === 'trip-plans' &&
              (!trips || trips.length === 0) && <MyTripsEmpty />}
            {selectedTab === 'trip-plans' && trips && trips.length > 0 && (
              <MyTripsListGrid
                trips={trips}
                source="trip_drawer"
                isVisibleCreateNewTripTile={false}
                className="drawer"
              />
            )}
            {selectedTab === 'saved-items' && !hasTripItems && (
              <EmptySavedItemsContainer
                gap={16}
                direction="vertical"
                horizontalAlign="center"
              >
                <LineHeartIcon size="L" />
                <Group gap={8} direction="vertical" horizontalAlign="center">
                  <Heading variant="heading4" align="center">
                    You have no saved items
                  </Heading>
                  <BodyText
                    variant="medium"
                    colour="neutral-three"
                    align="center"
                  >
                    Search our exclusive hotel offers, explore trip ideas in the
                    Explore area or add bookings you've made elsewhere.
                  </BodyText>
                </Group>
              </EmptySavedItemsContainer>
            )}
            {trips && selectedTab === 'saved-items' && hasTripItems && (
              <SavedItemsList trips={trips} />
            )}
          </Group>
        </ModalContent>
      </ModalBody>
      {selectedTab === 'trip-plans' && (
        <ModalFooter
          primaryActionProps={{
            children: trips && trips.length > 0 ? 'Create new trip' : undefined,
            onClick: createNewTrip,
            startIcon: <LinePlusCircleIcon />,
          }}
        />
      )}
    </ModalBase>
  )
}

export default SavedDrawer
